import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/*
 * LYMI (2026-10-25): the flying icon must travel to the shopping bag icon that is actually visible in the navbar.
 * Causes fixed here (Horizon's original measured whichever `.header-actions__cart-icon` came first, through an
 * IntersectionObserver, wherever it was):
 * - The header is not sticky, so after scrolling the only bag icon is above the viewport and the icon flew off the
 *   top of the screen. The destination is now resolved when the flight starts, and it must be rendered, not
 *   inside an inert area or closed dialog, and have its centre inside the viewport; otherwise there is no flight
 *   (the normal "added" feedback is unchanged).
 * - Hidden copies (display: none or zero size) are skipped rather than measured as 0,0 (the top-left corner).
 * - Both points are measured with getBoundingClientRect at the same moment (viewport coordinates, matching the
 *   element's position: fixed) and corrected for a transformed or filtered ancestor that would move the fixed
 *   element's origin.
 * - The end point is the icon's centre (the old 10px downward offset is removed in snippets/fly-to-cart-styles).
 * - Reduced motion: no flight. A new flight removes any earlier icon still in the air, so rapid repeats leave
 *   nothing behind, and every flight removes itself after its animation (or a short safety timeout).
 */

const NAVBAR_BAG_SELECTOR = '#header-component .header-actions__cart-icon, .header-actions__cart-icon';
const SAFETY_REMOVE_MS = 2000;

/**
 * @param {DOMRect} rect
 * @returns {boolean} Whether the rectangle's centre is inside the viewport.
 */
function centreInViewport(rect) {
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
  const viewportHeight = window.innerHeight;
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;
  return x >= 0 && x <= viewportWidth && y >= 0 && y <= viewportHeight;
}

/**
 * @param {Element} element
 * @returns {boolean} Whether the element is rendered and can be seen (not display: none, hidden, transparent or
 * zero-sized, and not inside an inert area or closed dialog).
 */
function isRendered(element) {
  if (!(element instanceof HTMLElement) || !element.isConnected) return false;
  if (element.closest('[inert], dialog:not([open]), [hidden]')) return false;
  if (typeof element.checkVisibility === 'function') {
    if (!element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
  } else {
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
  }
  const rect = element.getBoundingClientRect();
  return rect.width >= 1 && rect.height >= 1;
}

/**
 * The navbar shopping bag icon the customer can see right now, or null (no flight then).
 * @returns {HTMLElement | null}
 */
export function findVisibleCartIcon() {
  const candidates = new Set(document.querySelectorAll(NAVBAR_BAG_SELECTOR));
  for (const candidate of candidates) {
    if (!isRendered(candidate)) continue;
    if (!centreInViewport(candidate.getBoundingClientRect())) continue;
    return /** @type {HTMLElement} */ (candidate);
  }
  return null;
}

/** @returns {boolean} */
function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/**
 * Where a position: fixed element's 0,0 actually is. Normally the viewport corner; a transformed, filtered or
 * contained ancestor of <body> content moves it, so its offset is measured and subtracted.
 * @returns {{x: number, y: number}}
 */
function fixedOrigin() {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;left:0;top:0;width:0;height:0;margin:0;padding:0;border:0;visibility:hidden;pointer-events:none;';
  document.body.appendChild(probe);
  const rect = probe.getBoundingClientRect();
  probe.remove();
  return { x: rect.left, y: rect.top };
}

/**
 * Starts a flight from `source` to the visible navbar bag icon. Call only after a successful addition.
 * @param {{source: Element | null | undefined, image?: string | null, className?: string, useSourceSize?: boolean, startOpacity?: string}} options
 * @returns {boolean} Whether a flight was started.
 */
export function flyToCart({ source, image, className = 'fly-to-cart--main', useSourceSize = false, startOpacity }) {
  if (prefersReducedMotion()) return false;
  if (!source || !image || !isRendered(source)) return false;

  const destination = findVisibleCartIcon();
  if (!destination) return false;

  // Rapid repeats: only the newest icon flies
  document.querySelectorAll('fly-to-cart').forEach((element) => element.remove());

  const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
  flyToCartElement.classList.add(className);
  flyToCartElement.style.setProperty('background-image', `url(${JSON.stringify(String(image))})`);
  if (startOpacity !== undefined) flyToCartElement.style.setProperty('--start-opacity', startOpacity);
  flyToCartElement.useSourceSize = useSourceSize;
  flyToCartElement.source = source;
  flyToCartElement.destination = destination;
  document.body.appendChild(flyToCartElement);
  return true;
}

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  /** @type {number | undefined} */
  #safetyTimeout;

  connectedCallback() {
    super.connectedCallback();

    // Measured now, together, in viewport coordinates; the destination is re-checked so a stale or hidden icon
    // never becomes the target
    const destination =
      this.destination && isRendered(this.destination) && centreInViewport(this.destination.getBoundingClientRect())
        ? this.destination
        : findVisibleCartIcon();

    if (!this.source || !destination || !isRendered(this.source) || prefersReducedMotion()) {
      this.remove();
      return;
    }

    this.#safetyTimeout = window.setTimeout(() => this.remove(), SAFETY_REMOVE_MS);
    this.#animate(this.source.getBoundingClientRect(), destination.getBoundingClientRect(), fixedOrigin());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#safetyTimeout) window.clearTimeout(this.#safetyTimeout);
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRect} sourceRect - The source's viewport rectangle.
   * @param {DOMRect} destinationRect - The visible bag icon's viewport rectangle.
   * @param {{x: number, y: number}} origin - Where this fixed element's 0,0 is in the viewport.
   */
  #animate = async (sourceRect, destinationRect, origin) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - origin.x,
      y: sourceRect.top + sourceRect.height / 2 - origin.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - origin.x,
      y: destinationRect.top + destinationRect.height / 2 - origin.y,
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
